import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiError } from "@/context/AuthContext";
import api from "@/lib/api";
import { Loader2, KeyRound, LogOut, BadgeCheck, BadgePercent, Info } from "lucide-react";
import { toast } from "sonner";

const BASE = 350;

export default function Access() {
  const { refresh, logout } = useAuth();
  const nav = useNavigate();
  const refCode = (localStorage.getItem("up_ref") || "").trim().toUpperCase();
  const [info, setInfo] = useState(null);
  const [resolving, setResolving] = useState(!!refCode);
  const [buying, setBuying] = useState(false);
  const [error, setError] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!refCode) { setResolving(false); return; }
    let alive = true;
    api.get(`/referral/code/${encodeURIComponent(refCode)}`)
      .then(({ data }) => { if (alive) setInfo(data); })
      .catch(() => { if (alive) setInfo({ valid: false }); })
      .finally(() => { if (alive) setResolving(false); });
    return () => { alive = false; };
  }, [refCode]);

  const isDiscount = info?.valid && info.type === "discount";
  const price = isDiscount ? info.amount : BASE;

  const buy = async () => {
    setBuying(true); setError("");
    try { const { data } = await api.post("/payments/checkout", { referral_code: refCode }); window.location.assign(data.invoice_url); }
    catch (e) { setError(apiError(e.response?.data?.detail) || "Błąd płatności"); setBuying(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/access/redeem", { code });
      await refresh();
      toast.success("Licencja aktywowana");
      nav("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background terminal-bg p-6">
      <div className="w-full max-w-md fade-up">
        <div className="tmc-card p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center"><KeyRound className="w-5 h-5 text-primary" /></div>
            <div>
              <h1 className="font-head text-xl font-bold text-white">Aktywuj licencję</h1>
              <p className="text-sm text-muted-foreground">Kup dostęp lub wpisz kod licencyjny</p>
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-primary/30 bg-primary/5 p-4" data-testid="buy-access-card">
            <div className="flex items-baseline justify-between mb-1">
              <span className="font-head font-bold text-white">Dostęp dożywotni</span>
              <span className="font-mono gold-text text-lg flex items-baseline gap-1.5" data-testid="buy-price">
                {isDiscount && <span className="text-xs text-muted-foreground line-through decoration-muted-foreground/60">{BASE}</span>}
                {price} <span className="text-xs text-muted-foreground">USDC · Solana</span>
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground mb-3">Płatność w USDC (sieć Solana) przez NOWPayments. Dostęp aktywuje się automatycznie po potwierdzeniu płatności.</p>

            {resolving ? (
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground py-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sprawdzanie kodu z linku…</div>
            ) : refCode && info?.valid ? (
              <div className="space-y-2">
                <div className={`flex items-center gap-2 rounded-lg px-3 py-2.5 border ${isDiscount ? "border-accent/40 bg-accent/10" : "border-primary/40 bg-primary/10"}`} data-testid="buy-applied-code">
                  {isDiscount ? <BadgePercent className="w-4 h-4 text-accent shrink-0" /> : <BadgeCheck className="w-4 h-4 text-primary shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-sm text-white truncate">{refCode}</div>
                    <div className="text-[11px] text-muted-foreground">{isDiscount ? "Kod rabatowy — zniżka zastosowana (300 USDC)" : "Kod polecający zastosowany — pełna cena"}</div>
                  </div>
                </div>
                {error && <div className="text-[11px] text-rose-400" data-testid="buy-error">{error}</div>}
                <button onClick={buy} disabled={buying} data-testid="buy-access-btn"
                  className="w-full bg-primary hover:opacity-90 text-black font-bold rounded-lg px-4 py-2.5 flex items-center justify-center gap-2 disabled:opacity-60">
                  {buying && <Loader2 className="w-4 h-4 animate-spin" />} Kup dostęp
                </button>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-lg border border-border bg-secondary/40 px-3 py-2.5 text-[11px] text-muted-foreground" data-testid="buy-no-code">
                <Info className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                {refCode ? "Kod z Twojego linku jest nieprawidłowy lub nieaktywny. Otwórz ponownie link otrzymany od sprzedawcy." : "Aby kupić dostęp, otwórz link polecający otrzymany od sprzedawcy — cena i kod zastosują się automatycznie."}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 my-5 text-xs text-muted-foreground"><div className="flex-1 h-px bg-border" /> lub kod licencyjny <div className="flex-1 h-px bg-border" /></div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground font-mono">Kod licencyjny</label>
              <input value={code} onChange={(e) => setCode(e.target.value)} required data-testid="access-code-input" placeholder="Kod licencyjny"
                className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-white font-mono outline-none focus:border-primary" />
            </div>
            {error && <div className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2" data-testid="access-error">{error}</div>}
            <button type="submit" disabled={busy} data-testid="access-code-submit"
              className="w-full bg-primary hover:opacity-90 text-black font-bold rounded-lg px-4 py-2.5 transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Aktywuj
            </button>
          </form>
          <button onClick={doLogout} className="w-full mt-3 text-sm text-muted-foreground hover:text-white flex items-center justify-center gap-2" data-testid="access-logout">
            <LogOut className="w-4 h-4" /> Wyloguj
          </button>
        </div>
      </div>
    </div>
  );
}
